import { c as _c } from "react/compiler-runtime";
import type { PointerEvent, RefObject } from 'react';
import { useRef } from 'react';
const MOBILE_QUERY = '(width < 600px)';
const INTERACTIVE_SELECTOR = 'button, a, input, select, textarea';
const MAX_CLOSE_DISTANCE = 120;
const CLOSE_DISTANCE_RATIO = 0.3;
const CLOSE_VELOCITY = 0.5; // px per ms
// a flick only counts if the pointer was still moving when released
const FLICK_TIMEOUT = 100;
interface IDragState {
  pointerId: number;
  startY: number;
  offset: number;
  lastY: number;
  lastTime: number;
  velocity: number;
}
export interface IDragHandlers {
  onPointerDown: (event: PointerEvent<HTMLElement>) => void;
  onPointerMove: (event: PointerEvent<HTMLElement>) => void;
  onPointerUp: (event: PointerEvent<HTMLElement>) => void;
  onPointerCancel: (event: PointerEvent<HTMLElement>) => void;
  onLostPointerCapture: (event: PointerEvent<HTMLElement>) => void;
}
export const useDragToClose = dialogRef => {
  const $ = _c(16);
  const dragRef = useRef(null);
  let t0;
  if ($[0] !== dialogRef) {
    t0 = event => {
      const dialogEl = dialogRef.current;
      if (!dialogEl || !event.isPrimary || event.button !== 0 || !window.matchMedia(MOBILE_QUERY).matches || (event.target as HTMLElement).closest(INTERACTIVE_SELECTOR)) {
        return;
      }
      event.currentTarget.setPointerCapture(event.pointerId);
      dragRef.current = {
        pointerId: event.pointerId,
        startY: event.clientY,
        offset: 0,
        lastY: event.clientY,
        lastTime: event.timeStamp,
        velocity: 0
      };
      dialogEl.dataset.dragging = "";
    };
    $[0] = dialogRef;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const onPointerDown = t0;
  let t1;
  if ($[2] !== dialogRef) {
    t1 = event_0 => {
      const drag = dragRef.current;
      if (!drag || drag.pointerId !== event_0.pointerId) {
        return;
      }
      const elapsed = event_0.timeStamp - drag.lastTime;
      if (elapsed > 0) {
        drag.velocity = (event_0.clientY - drag.lastY) / elapsed;
      }
      drag.lastY = event_0.clientY;
      drag.lastTime = event_0.timeStamp;
      drag.offset = Math.max(0, event_0.clientY - drag.startY);
      dialogRef.current?.style.setProperty("--_drag-offset", `${drag.offset}px`);
    };
    $[2] = dialogRef;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const onPointerMove = t1;
  let t2;
  if ($[4] !== dialogRef) {
    t2 = (event_1, cancelled) => {
      const drag_0 = dragRef.current;
      const dialogEl_0 = dialogRef.current;
      if (!drag_0 || drag_0.pointerId !== event_1.pointerId) {
        return;
      }
      dragRef.current = null;
      if (!dialogEl_0) {
        return;
      }
      delete dialogEl_0.dataset.dragging;
      dialogEl_0.style.removeProperty("--_drag-offset");
      const closeDistance = Math.min(MAX_CLOSE_DISTANCE, dialogEl_0.offsetHeight * CLOSE_DISTANCE_RATIO);
      const isFlick = drag_0.velocity > CLOSE_VELOCITY && event_1.timeStamp - drag_0.lastTime < FLICK_TIMEOUT;
      if (!cancelled && (drag_0.offset > closeDistance || isFlick)) {
        dialogEl_0.close();
      }
    };
    $[4] = dialogRef;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const endDrag = t2;
  let t3;
  let t4;
  let t5;
  if ($[6] !== endDrag) {
    t3 = event_2 => endDrag(event_2, false);
    t4 = event_3 => endDrag(event_3, true);
    t5 = event_4 => endDrag(event_4, true);
    $[6] = endDrag;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
  } else {
    t3 = $[7];
    t4 = $[8];
    t5 = $[9];
  }
  let t6;
  if ($[10] !== onPointerDown || $[11] !== onPointerMove || $[12] !== t3 || $[13] !== t4 || $[14] !== t5) {
    t6 = {
      onPointerDown,
      onPointerMove,
      onPointerUp: t3,
      onPointerCancel: t4,
      onLostPointerCapture: t5
    };
    $[10] = onPointerDown;
    $[11] = onPointerMove;
    $[12] = t3;
    $[13] = t4;
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  return t6;
};